import { ComponentProps, forwardRef } from "react";
import { Slot, Slottable } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { LoaderCircle } from "lucide-react";
import { cn } from "~/lib/utils";

const buttonVariants = cva(
  "inline-flex relative items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive cursor-pointer",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground shadow-xs hover:bg-purple-800",
        destructive:
          "bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "text-neutral-950 border border-neutral-200 bg-transparent hover:bg-neutral-100 rounded-lg dark:bg-input/30 dark:border-input dark:hover:bg-input/50",
        secondary: "bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
        link: "text-primary underline-offset-4 hover:underline",
      },
      loading: {
        true: "text-transparent pointer-events-none",
        false: "",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9",
      },
    },
    compoundVariants: [
      { variant: "default", loading: true, className: "bg-primary/60" },
      { variant: "destructive", loading: true, className: "bg-destructive/60" },
      {
        variant: "outline",
        loading: true,
        className: "border-neutral-300 [&_.spinner]:stroke-gray-500",
      },
      { variant: "secondary", loading: true, className: "[&_.spinner]:stroke-gray-500" },
      { variant: "ghost", loading: true, className: "[&_.spinner]:stroke-primary/60" },
      { variant: "link", loading: true, className: "[&_.spinner]:stroke-primary/60" },
    ],
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps extends ComponentProps<"button">, VariantProps<typeof buttonVariants> {
  buttonRef?: React.Ref<HTMLButtonElement>;
  loading?: boolean;
  asChild?: boolean;
}

function Button({
  className,
  variant,
  size,
  asChild = false,
  loading,
  children,
  buttonRef,
  type = "button",
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      data-slot="button"
      ref={buttonRef}
      className={cn(buttonVariants({ variant, size, loading, className }))}
      type={type}
      {...(props as any)}
    >
      <Slottable>{children}</Slottable>
      {loading && (
        <div className="absolute w-full h-full grid place-items-center">
          <LoaderCircle className="spinner animate-spin stroke-white opacity-100" />
        </div>
      )}
    </Comp>
  );
}

const ForwardedRefButton = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  return <Button buttonRef={ref} {...props} />;
});
ForwardedRefButton.displayName = "Button";

export { ForwardedRefButton as Button, buttonVariants };
